import React from 'react';
import * as _ from 'lodash';
import { IconMinus, IconPlus } from '../../assets/icons';
import './ManageGuestCount.scss';
import { Button } from '../Button/Button';
import { ReceiptTotalTitle } from '../Receipt/ReceiptTotalTitle';
import { useIntl } from 'react-intl';
import { CurrencyUtils, TranslationUtils } from '../../utils';
import { Count, OccupancyCost, OccupancyCounts, OccupancyKeys } from '../../types/occupancy';

interface ManageGuestCountProps {
    counts: OccupancyCounts;
    maxCount: { max: number | null; rules?: string[] };
    onChange: (counts: OccupancyCounts) => void;
    onReset: () => void;
    cost?: OccupancyCost;
    isLoading?: boolean;
    isResetDisabled?: boolean;
    isError?: boolean;
    isDogMode?: boolean;
}

export const ManageGuestCount: React.FC<ManageGuestCountProps> = (props) => {
    const { counts, cost, maxCount, onChange, onReset, isLoading, isResetDisabled, isError, isDogMode } = props;
    const intl = useIntl();

    const maxCountRules = new Set(maxCount.rules || []);
    const currentCount = _.reduce(
        counts,
        (acc, curr, key) => {
            if (maxCountRules.size == 0 || maxCountRules.has(key)) {
                return acc + curr.count;
            }

            return acc;
        },
        0
    );
    const isMaxReached = currentCount >= maxCount.max!;

    const CountRow = (row: { id: OccupancyKeys; count: Count }) => {
        const { id, count } = row;
        return (
            <div className="manage-guest-count-row count-row">
                <div className="body-bold row-label">{count.name}</div>
                <CountControl id={id} count={count} />
            </div>
        );
    };

    const CostRow = (cost: OccupancyCost) => {
        return (
            <React.Fragment>
                {cost.previousBalance && cost.previousBalance.total > 0 && (
                    <React.Fragment>
                        {cost.adjustment && (
                            <div className="manage-guest-count-count-previous-balance">
                                <div className={'body-small'}>{TranslationUtils.get('sub_total')}</div>
                                <div className={'body-small'}>
                                    {CurrencyUtils.formatCurrency(cost.adjustment.total, cost.adjustment.currency)}
                                </div>
                            </div>
                        )}
                        <div className="manage-guest-count-count-previous-balance">
                            <div className={'body-small'}>{TranslationUtils.get('previous_balance')}</div>
                            <div className={'body-small'}>
                                {CurrencyUtils.formatCurrency(cost.previousBalance.total, cost.previousBalance.currency)}
                            </div>
                        </div>
                    </React.Fragment>
                )}

                <ReceiptTotalTitle summary={cost.total} convertedSummary={cost.total} locale={intl.locale} />
            </React.Fragment>
        );
    };

    const PetAdjustmentCostRow = (cost: OccupancyCost) => {
        const hasPreviousValance = cost.previousBalance;
        return (
            <React.Fragment>
                {hasPreviousValance && (
                    <React.Fragment>
                        <div className="manage-guest-count-count-previous-balance">
                            <div className={'body-small'}>{TranslationUtils.get('previous_balance')}</div>
                            <div className={'body-small'}>
                                {CurrencyUtils.formatCurrency(cost.previousBalance!.total, cost.previousBalance!.currency)}
                            </div>
                        </div>

                        {cost.adjustment && (
                            <div className="manage-guest-count-count-previous-balance">
                                <div className={'body-small'}>{TranslationUtils.get('pet_refund')}</div>
                                <div className={'body-small'}>
                                    {CurrencyUtils.formatCurrency(cost.adjustment.total, cost.adjustment.currency)}
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                )}

                <ReceiptTotalTitle
                    summary={cost.total}
                    withoutBackground={!hasPreviousValance}
                    convertedSummary={cost.total}
                    locale={intl.locale}
                    text={hasPreviousValance ? TranslationUtils.get('new_balance_due', { currency: cost.total.currency }) : undefined}
                />
            </React.Fragment>
        );
    };

    const CountControl = (control: { id: OccupancyKeys; count: Count }) => {
        const { id, count } = control;

        const compliesWithMin = count.min === undefined || count.count - 1 >= count.min;
        const compliesWithMax = count.max === undefined || count.count + 1 <= count.max;

        const canDecrease = !isError && !isLoading && count.count > 0 && compliesWithMin;
        const canIncrease = !isError && !isLoading && compliesWithMax && (!isMaxReached || !maxCountRules.has(id));

        const current = counts[id];

        if (!current) {
            return null;
        }

        const handleDecrease = () => {
            const updated: Count = { ...current, count: current.count - 1 };
            onChange({ ...counts, [id]: updated });
        };

        const handleIncrease = () => {
            const updated: Count = { ...current, count: current.count + 1 };
            onChange({ ...counts, [id]: updated });
        };

        return (
            <div className="manage-guest-count-control">
                <div className={`control-button ${!canDecrease ? 'disabled' : ''}`} onClick={canDecrease ? handleDecrease : undefined}>
                    <IconMinus />
                </div>
                <div className="body-regular control-text">{count.count}</div>
                <div className={`control-button ${!canIncrease ? 'disabled' : ''}`} onClick={canIncrease ? handleIncrease : undefined}>
                    <IconPlus />
                </div>
            </div>
        );
    };

    return (
        <div className="manage-guest-count">
            <div className="manage-guest-overview">
                {!isDogMode ? (
                    <p className={'body-small'}>
                        {TranslationUtils.get('max_occupancy_modify_guests', {
                            max_occupancy: maxCount.max,
                            guests:
                                maxCount.max! > 1
                                    ? TranslationUtils.get('guests').toLowerCase()
                                    : TranslationUtils.get('guest').toLowerCase(),
                        })}
                    </p>
                ) : (
                    <div className={'col-5'}></div>
                )}
                <Button className={'body-regular'} label={'Reset'} onClick={onReset} disabled={isResetDisabled || isLoading} />
            </div>
            <React.Fragment>
                {_.keys(counts).map((key) => (
                    <CountRow id={key as OccupancyKeys} count={counts[key as OccupancyKeys]} />
                ))}
            </React.Fragment>
            <React.Fragment>{cost && (isDogMode ? <PetAdjustmentCostRow {...cost} /> : <CostRow {...cost} />)}</React.Fragment>
        </div>
    );
};
